// src/utils/authErrorHandler.ts
export interface AuthError {
  code: string;
  message: string;
  details?: any;
  timestamp: Date;
  userId?: string;
  action?: string;
}

export class AuthErrorHandler {
  private static instance: AuthErrorHandler;
  private errorLog: AuthError[] = [];

  static getInstance(): AuthErrorHandler {
    if (!AuthErrorHandler.instance) {
      AuthErrorHandler.instance = new AuthErrorHandler();
    }
    return AuthErrorHandler.instance;
  }

  // Create standardized auth error
  createError(
    code: string,
    message: string,
    details?: any,
    userId?: string,
    action?: string
  ): AuthError {
    const error: AuthError = {
      code,
      message,
      details,
      timestamp: new Date(),
      userId,
      action,
    };

    this.logError(error);
    return error;
  }

  // Log error for debugging and monitoring
  private logError(error: AuthError): void {
    this.errorLog.push(error);
    
    // Keep only last 100 errors to prevent memory issues
    if (this.errorLog.length > 100) {
      this.errorLog = this.errorLog.slice(-100);
    }

    // Suppress expected errors (like REFRESH_FAILED during session restore)
    // These are normal when tokens are invalid/expired
    const isExpectedError = 
      error.code === 'REFRESH_FAILED' ||
      (error.details?.originalError as any)?.message?.includes('400') ||
      (error.details?.originalError as any)?.message?.includes('Bad Request');

    // Only log unexpected errors to console
    if (process.env.NODE_ENV === 'development' && !isExpectedError) {
      console.error('Auth Error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        timestamp: error.timestamp,
        userId: error.userId,
        action: error.action,
      });
    }
  }

  // Handle authentication errors
  handleAuthError(error: any, context?: string): AuthError {
    let authError: AuthError;

    if (error.response) {
      // API error response
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 401:
          authError = this.createError(
            'UNAUTHORIZED',
            data?.message || 'Authentication required',
            { status, response: data },
            undefined,
            context
          );
          break;
        case 403:
          authError = this.createError(
            'FORBIDDEN',
            data?.message || 'Access denied',
            { status, response: data },
            undefined,
            context
          );
          break;
        case 429:
          authError = this.handleRateLimitError(error, context);
          break;
        case 500:
          authError = this.createError(
            'SERVER_ERROR',
            'Server error. Please try again later.',
            { status, response: data },
            undefined,
            context
          );
          break;
        default:
          authError = this.createError(
            'API_ERROR',
            data?.message || 'An error occurred',
            { status, response: data },
            undefined,
            context
          );
      }
    } else if (error.message) {
      // Network or other error
      if (error.message.includes('Network Error') || error.message.includes('fetch')) {
        authError = this.createError(
          'NETWORK_ERROR',
          'Network error. Please check your connection.',
          { originalError: error.message },
          undefined,
          context
        );
      } else if (error.message.includes('timeout')) {
        authError = this.createError(
          'TIMEOUT_ERROR',
          'Request timeout. Please try again.',
          { originalError: error.message },
          undefined,
          context
        );
      } else {
        authError = this.createError(
          'UNKNOWN_ERROR',
          error.message,
          { originalError: error },
          undefined,
          context
        );
      }
    } else {
      // Unknown error
      authError = this.createError(
        'UNKNOWN_ERROR',
        'An unexpected error occurred',
        { originalError: error },
        undefined,
        context
      );
    }

    return authError;
  }

  // Handle token-related errors
  handleTokenError(error: any, context?: string): AuthError {
    let authError: AuthError;

    if (error.message?.includes('expired')) {
      authError = this.createError(
        'TOKEN_EXPIRED',
        'Your session has expired. Please log in again.',
        { originalError: error },
        undefined,
        context
      );
    } else if (error.message?.includes('invalid')) {
      authError = this.createError(
        'TOKEN_INVALID',
        'Invalid authentication token. Please log in again.',
        { originalError: error },
        undefined,
        context
      );
    } else if (error.message?.includes('refresh')) {
      authError = this.createError(
        'REFRESH_FAILED',
        'Failed to refresh authentication token. Please log in again.',
        { originalError: error },
        undefined,
        context
      );
    } else {
      authError = this.handleAuthError(error, context);
    }

    return authError;
  }

  // Handle rate limiting errors with detailed information
  handleRateLimitError(error: any, context?: string): AuthError {
    const retryAfter = error.response?.headers?.["retry-after"];
    const resetTime = error.response?.data?.resetTime;
    const violationCount = error.response?.data?.violationCount;
    const delayLevel = error.response?.data?.delayLevel;
    const description = error.response?.data?.description;

    let message = "Too many requests. Please try again later.";
    let code = "RATE_LIMITED";

    if (retryAfter) {
      const minutes = Math.ceil(parseInt(retryAfter) / 60);
      message = `Too many attempts. Please try again in ${minutes} minute${
        minutes > 1 ? "s" : ""
      }.`;
    }

    // Check if it's a progressive delay
    if (delayLevel && delayLevel > 0) {
      code = "PROGRESSIVE_DELAY";
      if (description) {
        message = description;
      } else {
        message = `Progressive delay active. Please wait ${retryAfter || 60} seconds before trying again.`;
      }
    }

    return this.createError(
      code,
      message,
      {
        status: 429,
        response: error.response?.data,
        retryAfter,
        resetTime,
        violationCount,
        delayLevel,
        description,
      },
      undefined,
      context
    );
  }

  // Handle permission errors
  handlePermissionError(requiredRole: string, userRole: string, context?: string): AuthError {
    return this.createError(
      'INSUFFICIENT_PERMISSIONS',
      `Access denied. Required role: ${requiredRole}, Your role: ${userRole}`,
      { requiredRole, userRole },
      undefined,
      context
    );
  }

  // Get user-friendly error message
  getUserFriendlyMessage(error: AuthError): string {
    switch (error.code) {
      case 'UNAUTHORIZED':
        return 'Please log in to continue.';
      case 'FORBIDDEN':
        return 'You don\'t have permission to perform this action.';
      case 'TOKEN_EXPIRED':
        return 'Your session has expired. Please log in again.';
      case 'TOKEN_INVALID':
        return 'Invalid session. Please log in again.';
      case 'REFRESH_FAILED':
        return 'Session refresh failed. Please log in again.';
      case 'RATE_LIMITED':
        return 'Too many requests. Please wait a moment and try again.';
      case 'PROGRESSIVE_DELAY':
        return error.message || 'Progressive delay active. Please wait before trying again.';
      case 'NETWORK_ERROR':
        return 'Network error. Please check your internet connection.';
      case 'TIMEOUT_ERROR':
        return 'Request timeout. Please try again.';
      case 'SERVER_ERROR':
        return 'Server error. Please try again later.';
      case 'INSUFFICIENT_PERMISSIONS':
        return 'You don\'t have the required permissions for this action.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  // Get error severity level
  getErrorSeverity(error: AuthError): 'low' | 'medium' | 'high' | 'critical' {
    switch (error.code) {
      case 'TOKEN_EXPIRED':
      case 'TOKEN_INVALID':
      case 'REFRESH_FAILED':
        return 'medium';
      case 'UNAUTHORIZED':
      case 'FORBIDDEN':
      case 'INSUFFICIENT_PERMISSIONS':
        return 'high';
      case 'SERVER_ERROR':
      case 'NETWORK_ERROR':
        return 'critical';
      case 'RATE_LIMITED':
      case 'PROGRESSIVE_DELAY':
      case 'TIMEOUT_ERROR':
        return 'low';
      default:
        return 'medium';
    }
  }

  // Check if error requires logout
  requiresLogout(error: AuthError): boolean {
    const logoutCodes = [
      'TOKEN_EXPIRED',
      'TOKEN_INVALID',
      'REFRESH_FAILED',
      'UNAUTHORIZED',
    ];
    return logoutCodes.includes(error.code);
  }

  // Get recent errors for debugging
  getRecentErrors(limit: number = 10): AuthError[] {
    return this.errorLog.slice(-limit);
  }

  // Clear error log
  clearErrorLog(): void {
    this.errorLog = [];
  }
}

// Export singleton instance
export const authErrorHandler = AuthErrorHandler.getInstance();

// Helper function to create auth error
export const createAuthError = (
  code: string,
  message: string,
  details?: any,
  userId?: string,
  action?: string
): AuthError => {
  return authErrorHandler.createError(code, message, details, userId, action);
};

// Helper function to handle auth error
export const handleAuthError = (error: any, context?: string): AuthError => {
  return authErrorHandler.handleAuthError(error, context);
};

// Helper function to handle token error
export const handleTokenError = (error: any, context?: string): AuthError => {
  return authErrorHandler.handleTokenError(error, context);
};

// Helper function to handle permission error
export const handlePermissionError = (
  requiredRole: string,
  userRole: string,
  context?: string
): AuthError => {
  return authErrorHandler.handlePermissionError(requiredRole, userRole, context);
};
